import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Building2, MapPin, Save, Loader2, Plus, User, ShieldCheck, ArrowLeft,
} from "lucide-react";
import {
  identityGet, identityPut, identityAddLocation, identitySetSuccessManager,
} from "@/lib/api";
import { toast } from "sonner";

/**
 * Business Identity Admin — /admin/identity
 * Editable canonical business record + locations + named success manager.
 */
export default function BusinessIdentityAdmin() {
  const [identity, setIdentity] = useState(null);
  const [locations, setLocations] = useState([]);
  const [form, setForm] = useState({});
  const [smForm, setSmForm] = useState({});
  const [locForm, setLocForm] = useState({ label: "", address: "", phone: "", hours: "" });
  const [busy, setBusy] = useState(false);
  const [addingLoc, setAddingLoc] = useState(false);
  const [savingSm, setSavingSm] = useState(false);

  useEffect(() => {
    document.title = "Business Identity — Admin — WebsiteTech.pro";
    load();
  }, []);

  const load = async () => {
    try {
      const r = await identityGet();
      setIdentity(r.identity);
      setLocations(r.locations || []);
      if (r.identity) {
        setForm({
          legal_name: r.identity.legal_name || "",
          display_name: r.identity.display_name || "",
          primary_category: r.identity.primary_category || "",
          website: r.identity.website || "",
          email: r.identity.email || "",
          description: r.identity.description || "",
          languages: (r.identity.languages || []).join(", "),
        });
        setSmForm(r.identity.success_manager || {});
      }
    } catch (e) {
      toast.error("Sign in required.");
    }
  };

  const saveIdentity = async () => {
    setBusy(true);
    try {
      await identityPut({
        ...form,
        languages: form.languages
          ? form.languages.split(",").map((s) => s.trim()).filter(Boolean)
          : undefined,
      });
      toast.success("Identity saved");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const addLocation = async () => {
    if (!locForm.label.trim()) { toast.error("Label required"); return; }
    setAddingLoc(true);
    try {
      await identityAddLocation(locForm);
      toast.success("Location added");
      setLocForm({ label: "", address: "", phone: "", hours: "" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    } finally { setAddingLoc(false); }
  };

  const saveSm = async () => {
    if (!smForm.name?.trim()) { toast.error("Manager name required"); return; }
    setSavingSm(true);
    try {
      await identitySetSuccessManager(smForm);
      toast.success("Success manager assigned");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingSm(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-identity-page">
      <Navbar />
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-4xl px-4 py-8">
          <Link to="/dashboard" className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-orange-600">
            <ArrowLeft className="h-3 w-3" /> Back to dashboard
          </Link>
          <div className="mt-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-orange-600">
            <Building2 className="h-4 w-4" /> Business Identity — Admin
          </div>
          <h1 className="mt-2 font-heading text-3xl font-black text-slate-900 sm:text-4xl">
            Canonical business record
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-600">
            One record — reused by websites, listings, schema, AI assistants
            and reports. Every field records provenance and update history.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-4xl space-y-6 px-4 py-8">
        <Card className="border-slate-200 bg-white p-6" data-testid="admin-identity-form">
          <div className="font-heading text-lg font-bold text-slate-900">Identity</div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <F label="Legal name" v={form.legal_name} onC={(v) => setForm({ ...form, legal_name: v })} t="admin-legal-name" />
            <F label="Display name" v={form.display_name} onC={(v) => setForm({ ...form, display_name: v })} t="admin-display-name" />
            <F label="Primary category" v={form.primary_category} onC={(v) => setForm({ ...form, primary_category: v })} t="admin-category" />
            <F label="Website" v={form.website} onC={(v) => setForm({ ...form, website: v })} t="admin-website" />
            <F label="Public email" v={form.email} onC={(v) => setForm({ ...form, email: v })} t="admin-email" />
            <F label="Languages (comma)" v={form.languages} onC={(v) => setForm({ ...form, languages: v })} t="admin-languages" />
          </div>
          <label className="mt-3 block text-xs font-semibold text-slate-700">
            Description
            <Textarea value={form.description || ""}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3} className="mt-1" data-testid="admin-description" />
          </label>
          <div className="mt-4 flex justify-end">
            <Button onClick={saveIdentity} disabled={busy}
              className="bg-orange-600 text-white hover:bg-orange-700" data-testid="admin-identity-save">
              {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
              Save identity
            </Button>
          </div>
        </Card>

        {/* Named human success manager */}
        <Card className="border-emerald-200 bg-emerald-50 p-6" data-testid="admin-success-manager">
          <div className="flex items-center gap-2 font-heading text-lg font-bold text-emerald-900">
            <ShieldCheck className="h-5 w-5" /> Named Success Manager
          </div>
          <p className="mt-1 text-xs text-emerald-800">
            Every managed tier includes a human strategist. Enter theirs so it
            shows on client-facing pages and reports.
          </p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <F label="Name" v={smForm.name} onC={(v) => setSmForm({ ...smForm, name: v })} t="admin-sm-name" />
            <F label="Email" v={smForm.email} onC={(v) => setSmForm({ ...smForm, email: v })} t="admin-sm-email" />
            <F label="Phone" v={smForm.phone} onC={(v) => setSmForm({ ...smForm, phone: v })} t="admin-sm-phone" />
            <F label="Photo URL" v={smForm.photo_url} onC={(v) => setSmForm({ ...smForm, photo_url: v })} t="admin-sm-photo" />
          </div>
          <label className="mt-3 block text-xs font-semibold text-emerald-900">
            Short bio
            <Textarea value={smForm.bio || ""}
              onChange={(e) => setSmForm({ ...smForm, bio: e.target.value })}
              rows={2} className="mt-1 bg-white" data-testid="admin-sm-bio" />
          </label>
          <div className="mt-4 flex justify-end">
            <Button onClick={saveSm} disabled={savingSm}
              className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="admin-sm-save">
              {savingSm ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <User className="mr-1 h-4 w-4" />}
              Assign success manager
            </Button>
          </div>
        </Card>

        {/* Locations */}
        <Card className="border-slate-200 bg-white p-6" data-testid="admin-locations">
          <div className="flex items-center gap-2 font-heading text-lg font-bold text-slate-900">
            <MapPin className="h-5 w-5" /> Locations ({locations.length})
          </div>
          {locations.length === 0 && <div className="mt-2 text-sm text-slate-500">No locations yet.</div>}
          <div className="mt-3 space-y-2">
            {locations.map((l) => (
              <div key={l._id} className="flex items-start justify-between rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm" data-testid={`admin-loc-${l._id}`}>
                <div>
                  <div className="font-semibold text-slate-900">{l.label} {l.is_primary && <Badge variant="outline" className="ml-1 text-[9px] uppercase">primary</Badge>}</div>
                  <div className="text-xs text-slate-500">{l.address || "—"} · {l.phone || "no phone"} · {l.hours || "hours n/a"}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 rounded-lg border border-dashed border-slate-300 p-3">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Add a location</div>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              <F label="Label" v={locForm.label} onC={(v) => setLocForm({ ...locForm, label: v })} t="admin-newloc-label" />
              <F label="Phone" v={locForm.phone} onC={(v) => setLocForm({ ...locForm, phone: v })} t="admin-newloc-phone" />
              <F label="Address" v={locForm.address} onC={(v) => setLocForm({ ...locForm, address: v })} t="admin-newloc-address" />
              <F label="Hours" v={locForm.hours} onC={(v) => setLocForm({ ...locForm, hours: v })} t="admin-newloc-hours" />
            </div>
            <div className="mt-2 flex justify-end">
              <Button size="sm" onClick={addLocation} disabled={addingLoc}
                variant="outline" data-testid="admin-newloc-add">
                {addingLoc ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Plus className="mr-1 h-3 w-3" />}
                Add location
              </Button>
            </div>
          </div>
        </Card>
      </section>

      <Footer />
    </div>
  );
}

function F({ label, v, onC, t }) {
  return (
    <label className="block text-xs font-semibold text-slate-700">
      {label}
      <Input value={v || ""} onChange={(e) => onC(e.target.value)} className="mt-1 bg-white" data-testid={t} />
    </label>
  );
}
